import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import './quotes.css';
import { api, ApiError, uploadFile } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { GeneralError, issueFor, PricingNav, QUOTE_SETTINGS_RESOURCE } from '../pricing/PricingLayout';
import { ConflictNotice } from '../ui/ConflictNotice';
import { useUnsavedGuard } from '../ui/unsaved';
import { FileDrop } from '../ui/FileDrop';

const SETTINGS_KEY = ['quotes', 'settings'] as const;
const FIELDS = ['companyName', 'footerDefault', 'emailSubject', 'emailBody', 'logoFileId', 'printTerms', 'printValidDays'] as const;

export type QuoteSettings = { companyName: string; footerDefault: string; emailSubject: string; emailBody: string; logoFileId: string | null; printTerms: string; printValidDays: number; version: number };
type Draft = Omit<QuoteSettings, 'version'>;
const draftOf = (s: QuoteSettings): Draft => ({ companyName: s.companyName, footerDefault: s.footerDefault, emailSubject: s.emailSubject, emailBody: s.emailBody, logoFileId: s.logoFileId, printTerms: s.printTerms, printValidDays: s.printValidDays });

/** "Ustawienia kosztorysu" under "Cenniki": the company name and signature of the PDF, the logo, the e-mail template. */
export function QuoteSettingsPage() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const settings = useQuery({ queryKey: SETTINGS_KEY, queryFn: () => api<QuoteSettings>('/api/quotes/settings') });
  const [base, setBase] = useState<QuoteSettings | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  useEffect(() => {
    if (settings.data && !base) {
      setBase(settings.data);
      setDraft(draftOf(settings.data));
    }
  }, [settings.data, base]);

  const save = useMutation({
    mutationFn: (body: Partial<Draft> & { version: number }) => api<QuoteSettings>('/api/quotes/settings', { method: 'PATCH', body }),
    onSuccess: (s) => {
      setBase(s);
      setDraft(draftOf(s));
      setSaved(true);
      queryClient.setQueryData(SETTINGS_KEY, s);
    },
  });

  // owner 2026-10-23 21:14: Escape or leaving with unsaved changes asks „Zapisz / Nie zapisuj / Anuluj”
  const changes = () => (base && draft ? Object.fromEntries(FIELDS.filter((k) => draft[k] !== base[k]).map((k) => [k, draft[k]])) : {});
  const guardRef = useUnsavedGuard({
    dirty: !!me && can(me, QUOTE_SETTINGS_RESOURCE, 'edit') && Object.keys(changes()).length > 0,
    save: async () => {
      if (!base) return false;
      await save.mutateAsync({ ...changes(), version: base.version });
      return true;
    },
  });
  if (!me) return null;
  const editable = can(me, QUOTE_SETTINGS_RESOURCE, 'edit');
  const conflict = save.error instanceof ApiError && save.error.status === 409;
  const set = (patch: Partial<Draft>) => {
    setSaved(false);
    setDraft((d) => (d ? { ...d, ...patch } : d));
  };
  const err = (path: string) => issueFor(save.error, path);

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!base || !draft) return;
    const changed = Object.fromEntries(FIELDS.filter((k) => draft[k] !== base[k]).map((k) => [k, draft[k]]));
    save.mutate({ ...changed, version: base.version });
  }
  function reload() {
    const current = (save.error as ApiError).current as QuoteSettings | undefined;
    if (current) {
      setBase(current);
      setDraft(draftOf(current));
    }
    save.reset();
  }
  async function pickLogo(file: File | undefined) {
    if (!file) return;
    setUploadError(null);
    setUploading(true);
    try {
      set({ logoFileId: await uploadFile(file) });
    } catch (error) {
      setUploadError(error instanceof ApiError ? error.message : 'Nie udało się wgrać pliku');
    } finally {
      setUploading(false);
    }
  }

  return (
    <section className="page" data-feature-id="quotes.settings">
      <div className="page-head">
        <h2>Cenniki</h2>
      </div>
      <PricingNav me={me} />
      <p className="muted">Ustawienia wspólne dla wszystkich kosztorysów: nagłówek i stopka PDF, logo oraz wiadomość do klienta.</p>
      {settings.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {settings.error && <p className="alert" role="alert">{settings.error instanceof ApiError ? settings.error.message : 'Nie udało się wczytać ustawień'}</p>}
      {draft && (
        <form ref={guardRef} className="panel form q-settings" aria-label="Ustawienia kosztorysu" onSubmit={submit}>
          <fieldset className="q-settings-group" disabled={!editable}>
            <legend>PDF kosztorysu</legend>
            <label className="field">
              <span>Nazwa firmy</span>
              <input value={draft.companyName} maxLength={200} required onChange={(e) => set({ companyName: e.target.value })} />
              {err('companyName') && <span className="field-error">{err('companyName')}</span>}
            </label>
            <label className="field">
              <span>Podpis („sporządził: …”)</span>
              <input value={draft.footerDefault} maxLength={1000} placeholder="np. Jan Kowalski, tel. 600 000 000, biuro@firma.pl" onChange={(e) => set({ footerDefault: e.target.value })} />
              <small className="hint">Nowy kosztorys i nowa wersja biorą ten podpis, gdy wersja nie ma własnego.</small>
              {err('footerDefault') && <span className="field-error">{err('footerDefault')}</span>}
            </label>
            <div className="field">
              <span id="q-logo-label">Logo</span>
              <div className="q-logo" aria-labelledby="q-logo-label">
                {draft.logoFileId ? <img src={`/api/files/${draft.logoFileId}`} alt="Logo na PDF" /> : <img src="/brand/mek-records-mark.svg" alt="Domyślny znak Mek Records" className="q-logo-default" />}
                {editable && (
                  <FileDrop accept="image/png,image/jpeg" disabled={uploading} onFiles={(files) => void pickLogo(files[0])}>
                    <div className="actions">
                      <label className="btn small">
                        {uploading ? 'Wgrywanie…' : draft.logoFileId ? 'Zmień logo' : 'Wgraj logo'}
                        <input type="file" accept="image/png,image/jpeg" hidden onChange={(e) => void pickLogo(e.target.files?.[0])} />
                      </label>
                      {draft.logoFileId && (
                        <button className="btn small" type="button" onClick={() => set({ logoFileId: null })}>
                          Użyj znaku Mek Records
                        </button>
                      )}
                    </div>
                  </FileDrop>
                )}
              </div>
              <small className="hint">PNG albo JPG. Bez własnego logo PDF ma znak Mek Records.</small>
              {(uploadError ?? err('logoFileId')) && <span className="field-error">{uploadError ?? err('logoFileId')}</span>}
            </div>
          </fieldset>
          {/* wzór F (owner 2026-10-30): the print view's terms and validity — the owner's */}
          <fieldset className="q-settings-group" disabled={!editable || !me.isOwner} data-feature-id="quotes.print_view">
            <legend>Podgląd wydruku (wzór F)</legend>
            <label className="field">
              <span>Warunki na kosztorysie</span>
              <textarea rows={3} value={draft.printTerms} maxLength={2000} onChange={(e) => set({ printTerms: e.target.value })} />
              <small className="hint">Można użyć: {'{vat}'} (stawka VAT wersji), {'{waznosc}'} (dni ważności). Zmienia je tylko właściciel.</small>
              {err('printTerms') && <span className="field-error">{err('printTerms')}</span>}
            </label>
            <label className="field">
              <span>Ważność kosztorysu (dni)</span>
              <input type="number" min={1} max={365} step={1} inputMode="numeric" value={Number.isNaN(draft.printValidDays) ? '' : draft.printValidDays} onChange={(e) => set({ printValidDays: e.target.valueAsNumber })} />
              <small className="hint">Na wydruku: „ważny do …” — data kosztorysu plus tyle dni.</small>
              {err('printValidDays') && <span className="field-error">{err('printValidDays')}</span>}
            </label>
          </fieldset>
          <fieldset className="q-settings-group" disabled={!editable}>
            <legend>Wiadomość do klienta</legend>
            <label className="field">
              <span>Temat e-maila</span>
              <input value={draft.emailSubject} maxLength={300} required onChange={(e) => set({ emailSubject: e.target.value })} />
              {err('emailSubject') && <span className="field-error">{err('emailSubject')}</span>}
            </label>
            <label className="field">
              <span>Treść e-maila</span>
              <textarea rows={6} value={draft.emailBody} maxLength={10000} onChange={(e) => set({ emailBody: e.target.value })} />
              <small className="hint">Można użyć: {'{wydarzenie}'}, {'{data}'}, {'{wersja}'}, {'{podpis}'}. Tak zaczyna się okno „Wyślij do klienta” w kosztorysie; przed wysłaniem można wszystko zmienić.</small>
              {err('emailBody') && <span className="field-error">{err('emailBody')}</span>}
            </label>
          </fieldset>
          {conflict && <ConflictNotice onReload={reload} />}
          <GeneralError error={save.error} fields={FIELDS} />
          {editable && (
            <div className="actions">
              <button className="btn primary" type="submit" disabled={save.isPending || uploading}>
                {save.isPending ? 'Zapisywanie…' : 'Zapisz ustawienia'}
              </button>
              {saved && (
                <span className="muted" role="status">
                  Zapisano.
                </span>
              )}
            </div>
          )}
        </form>
      )}
    </section>
  );
}
